<template>
  <div class="agent-flows-page full-container">
    <div class="agent-flows-layout">
      <div class="agent-flows-panel">
        <div class="agent-flows-toolbar">
          <div class="agent-flows-toolbar-text">
            <span class="agent-flows-toolbar-title">Agent 智能体</span>
            <span class="agent-flows-toolbar-desc">管理智能体流程，设计编排与在线运行</span>
          </div>
          <div class="agent-flows-toolbar-actions">
            <Input
              v-model="searchKeyword"
              search
              clearable
              placeholder="按名称搜索"
              style="width: 220px; margin-right: 8px;"
            />
            <Button type="success" icon="md-add" @click="openCreate">新建</Button>
            <Button icon="md-refresh" @click="fetchList">刷新</Button>
          </div>
        </div>
        <div class="agent-flows-table-wrap">
          <BaseTable
            ref="table"
            full
            class="agent-flows-main-table"
            body-scroll-x="hidden"
            :option="{ stripe: true }"
            :columns="columns"
            :data="items"
            :loading="loading"
            border
          />
        </div>
      </div>
    </div>

    <Modal
      v-model="formVisible"
      :title="formTitle"
      width="640"
      :loading="formSubmitting"
      @on-ok="handleFormOk"
      @on-cancel="formVisible = false"
    >
      <Form ref="formRef" :model="formData" :rules="formRules" :label-width="96">
        <FormItem label="名称" prop="name">
          <Input v-model="formData.name" placeholder="智能体名称" />
        </FormItem>
        <FormItem label="描述" prop="description">
          <Input v-model="formData.description" type="textarea" :rows="3" placeholder="简要描述" />
        </FormItem>
        <FormItem label="流程 JSON" prop="content_json">
          <Input
            v-model="formData.content_json"
            type="textarea"
            :rows="10"
            placeholder='{"nodes":[],"edges":[]}'
          />
        </FormItem>
      </Form>
    </Modal>

    <Drawer
      v-model="runVisible"
      :title="runTitle"
      width="680"
      :closable="true"
      class="agent-flow-run-drawer"
      @on-close="closeRun"
    >
      <div class="run-chat-panel">
        <div class="run-chat-messages" ref="chatMessagesRef">
          <div
            v-for="(msg, idx) in chatMessages"
            :key="idx"
            :class="['run-chat-msg', msg.role === 'user' ? 'msg-user' : 'msg-bot']"
          >
            <div class="msg-bubble" :class="msg.role">
              <template v-if="msg.role === 'user'">{{ msg.content }}</template>
              <template v-else>
                <div class="msg-markdown-wrap" :class="{ 'msg-streaming': msg.streaming }">
                  <BaseMarkdownView v-if="msg.content" :value="msg.content" />
                  <span v-else-if="msg.streaming" class="cursor-blink">...</span>
                </div>
              </template>
            </div>
          </div>
          <div v-if="chatSending && !streamingMessage" class="run-chat-msg msg-bot">
            <div class="msg-bubble bot typing">正在回复...</div>
          </div>
        </div>
        <div class="run-chat-input-wrap">
          <Input
            v-model="chatInput"
            type="textarea"
            :rows="3"
            placeholder="输入消息，Enter 发送"
            :disabled="chatSending"
            @keydown.enter.native.prevent="sendMessage"
          />
          <div class="run-chat-actions">
            <Button type="primary" :loading="chatSending" @click="sendMessage">发送</Button>
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<script>
import BaseMarkdownView from '@/components/base/MarkdownView';

const API_BASE = '/ai/agent-flows';
const CHAT_API = '/ai/chat';

function getContentJson(row) {
  if (!row) return '';
  const v = row.content_json != null ? row.content_json : row['content-json'];
  if (v == null || v === '') return '';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v, null, 2);
    } catch (e) {
      return String(v);
    }
  }
  return String(v);
}

function parseContentJson(text) {
  const raw = (text || '').trim();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return raw;
  }
}

export default {
  components: { BaseMarkdownView },
  data() {
    return {
      loading: false,
      allItems: [],
      searchKeyword: '',
      formVisible: false,
      formTitle: '新建智能体',
      formSubmitting: false,
      formEditId: null,
      formData: {
        name: '',
        description: '',
        content_json: '',
      },
      formRules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
      },
      runVisible: false,
      runRow: null,
      chatMessages: [],
      chatInput: '',
      chatSending: false,
      streamingMessage: false,
      columns: [
        { type: 'index', width: 56, title: '#' },
        {
          title: 'ID',
          key: 'id',
          width: 80,
          align: 'center',
        },
        {
          title: '名称',
          key: 'name',
          minWidth: 140,
        },
        {
          title: '描述',
          key: 'description',
          minWidth: 200,
          ellipsis: true,
          render: (h, { row }) => h('span', { attrs: { title: row.description || '' } }, row.description || '—'),
        },
        {
          title: '设计',
          key: 'design',
          width: 88,
          align: 'center',
          render: (h, { row }) => h('Button', {
            props: { size: 'small', type: 'primary', ghost: true, icon: 'md-git-network' },
            on: { click: () => this.openDesign(row) },
          }, '设计'),
        },
        {
          title: '运行',
          key: 'run',
          width: 88,
          align: 'center',
          render: (h, { row }) => h('Button', {
            props: { size: 'small', type: 'success', ghost: true, icon: 'md-play' },
            on: { click: () => this.openRun(row) },
          }, '运行'),
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          align: 'center',
          render: (h, { row }) => h('div', { class: 'agent-flows-action-cell' }, [
            h('Button', {
              props: { size: 'small', type: 'text' },
              style: { marginRight: '6px' },
              on: { click: () => this.openEdit(row) },
            }, '编辑'),
            h('Button', {
              props: { size: 'small', type: 'text' },
              on: { click: () => this.confirmDelete(row) },
            }, '删除'),
          ]),
        },
      ],
    };
  },
  computed: {
    session() {
      return this.$store.getters['session/session'] || {};
    },
    items() {
      const kw = (this.searchKeyword || '').trim();
      if (!kw) return this.allItems;
      return this.allItems.filter((row) => {
        const name = row && row.name != null ? String(row.name) : '';
        return name.includes(kw);
      });
    },
    runTitle() {
      const row = this.runRow;
      return row ? `运行 — ${row.name || row.id || ''}` : '运行';
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.fetchList();
    });
  },
  methods: {
    stripBearer(token) {
      if (!token) return '';
      const t = String(token);
      return t.startsWith('Bearer ') ? t.slice(7) : t;
    },
    getDesignUrl(row) {
      if (!row || row.id == null) return '';
      const base = (this.$api.defaults.baseURL || 'https://api.inbgz.com').replace(/\/$/, '');
      const token = this.stripBearer(this.session.token);
      return `${base}/public/miniapps/agent-flow?id=${encodeURIComponent(row.id)}&token=${encodeURIComponent(token)}`;
    },
    openDesign(row) {
      if (!row || row.id == null) return;
      const token = this.stripBearer(this.session.token);
      if (!token) {
        this.$Message.warning('请先登录');
        return;
      }
      const url = this.getDesignUrl(row);
      window.open(url, '_blank');
    },
    openRun(row) {
      this.runRow = row;
      this.chatMessages = [];
      this.chatInput = '';
      this.chatSending = false;
      this.streamingMessage = false;
      this.runVisible = true;
    },
    closeRun() {
      this.runRow = null;
      this.chatMessages = [];
      this.chatInput = '';
      this.chatSending = false;
      this.streamingMessage = false;
    },
    async fetchList() {
      this.loading = true;
      try {
        const res = await this.$api.get(API_BASE);
        let list = (res.data && res.data.data) || res.data || [];
        if (!Array.isArray(list) && list && Array.isArray(list.list)) list = list.list;
        this.allItems = Array.isArray(list) ? list : [];
      } catch (e) {
        this.$Message.error(e.message || '加载失败');
        this.allItems = [];
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.formTitle = '新建智能体';
      this.formEditId = null;
      this.formData = { name: '', description: '', content_json: '' };
      this.formVisible = true;
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetFields();
      });
    },
    openEdit(row) {
      this.formTitle = '编辑智能体';
      this.formEditId = row.id;
      this.formData = {
        name: row.name || '',
        description: row.description || '',
        content_json: getContentJson(row),
      };
      this.formVisible = true;
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetFields();
      });
    },
    async handleFormOk() {
      const valid = await this.$refs.formRef.validate();
      if (!valid) return false;
      this.formSubmitting = true;
      try {
        const payload = {
          name: this.formData.name.trim(),
          description: (this.formData.description || '').trim() || undefined,
          content_json: parseContentJson(this.formData.content_json),
        };
        if (this.formEditId != null) {
          await this.$api.patch(`${API_BASE}/${this.formEditId}`, payload);
          this.$Message.success('已保存');
        } else {
          await this.$api.post(API_BASE, payload);
          this.$Message.success('创建成功');
        }
        this.formVisible = false;
        await this.fetchList();
        return true;
      } catch (e) {
        this.$Message.error(e.message || '保存失败');
        return false;
      } finally {
        this.formSubmitting = false;
      }
    },
    confirmDelete(row) {
      this.$Modal.confirm({
        title: '删除确认',
        content: `确定删除「${row.name || row.id}」？`,
        onOk: async () => {
          try {
            await this.$api.delete(`${API_BASE}/${row.id}`);
            this.$Message.success('已删除');
            await this.fetchList();
          } catch (e) {
            this.$Message.error(e.message || '删除失败');
          }
        },
      });
    },
    sendMessage() {
      const text = (this.chatInput || '').trim();
      if (!text || this.chatSending || !this.runRow) return;
      this.chatMessages.push({ role: 'user', content: text });
      this.chatInput = '';
      this.chatSending = true;
      this.streamingMessage = false;
      this.$nextTick(() => this.scrollChatToBottom());

      const streamDone = (errMsg) => {
        const last = this.chatMessages[this.chatMessages.length - 1];
        if (last && last.streaming) {
          last.streaming = false;
          if (errMsg) last.content = errMsg;
        }
        this.streamingMessage = false;
        this.chatSending = false;
        this.$nextTick(() => this.scrollChatToBottom());
      };

      this.streamChat(text, streamDone).catch((e) => {
        const errMsg = (e && e.message) || '请求失败';
        this.chatMessages.push({ role: 'assistant', content: errMsg });
        streamDone();
      });
    },
    async streamChat(userText, streamDone) {
      const flowJson = getContentJson(this.runRow);
      const systemParts = [`你是智能体「${this.runRow.name || ''}」的运行助手。`];
      if (this.runRow.description) systemParts.push(`描述：${this.runRow.description}`);
      if (flowJson) systemParts.push(`流程定义：\n${flowJson}`);

      const messages = [
        { role: 'system', content: systemParts.join('\n\n') },
        ...this.chatMessages
          .filter((m) => m.role !== 'system')
          .map((m) => ({ role: m.role, content: m.content })),
      ];

      const url = (this.$api.defaults.baseURL || '').replace(/\/$/, '') + CHAT_API;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          ...(this.$api.defaults.headers || {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages,
          agent_flow_id: this.runRow.id,
        }),
        credentials: 'same-origin',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.msg || `HTTP ${res.status}`);
      }

      const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
      const isStream = contentType.indexOf('text/event-stream') !== -1
        || contentType.indexOf('stream') !== -1
        || contentType.indexOf('text/plain') !== -1;

      if (!res.body || !isStream) {
        const data = await res.json().catch(() => ({}));
        const reply = (data.data && data.data.content) || data.content || data.message || '';
        this.chatMessages.push({ role: 'assistant', content: reply || '暂无回复' });
        streamDone();
        return;
      }

      this.chatMessages.push({ role: 'assistant', content: '', streaming: true });
      const streamMsg = this.chatMessages[this.chatMessages.length - 1];
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      const isPlainText = contentType.indexOf('text/plain') !== -1;

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          if (isPlainText) {
            fullContent += chunk;
            this.$set(streamMsg, 'content', fullContent);
            this.$nextTick(() => this.scrollChatToBottom());
            continue;
          }

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.startsWith('data:')) {
              const raw = line.replace(/^data:\s*/, '').trim();
              if (raw === '[DONE]' || !raw) continue;
              try {
                const data = JSON.parse(raw);
                const delta = (data.choices && data.choices[0] && data.choices[0].delta && data.choices[0].delta.content)
                  || data.content
                  || data.delta
                  || (typeof data.text === 'string' ? data.text : '');
                if (delta) {
                  fullContent += delta;
                  this.$set(streamMsg, 'content', fullContent);
                  this.$nextTick(() => this.scrollChatToBottom());
                }
              } catch (_) { /* ignore */ }
            }
          }
        }
        if (isPlainText && buffer) fullContent += buffer;
        if (!fullContent.trim()) {
          const last = buffer.trim();
          if (last) {
            try {
              const data = JSON.parse(last);
              const c = (data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content)
                || data.content
                || '';
              if (c) fullContent = c;
            } catch (_) {
              fullContent = last;
            }
          }
          this.$set(streamMsg, 'content', fullContent || '暂无回复');
        }
      } finally {
        streamDone();
      }
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatMessagesRef;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.agent-flows-page.full-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.03) 0%, transparent 32%);
}
.agent-flows-layout {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 14px 32px 20px;
  box-sizing: border-box;
}
.agent-flows-panel {
  max-width: 89%;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--bg2, #fff);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.agent-flows-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.015);
}
.agent-flows-toolbar-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.agent-flows-toolbar-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text, #333);
}
.agent-flows-toolbar-desc {
  font-size: 12px;
  color: var(--subtext2);
}
.agent-flows-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.agent-flows-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
}
.agent-flows-table-wrap >>> .gz-table,
.agent-flows-table-wrap >>> .ivu-table-wrapper {
  height: 100%;
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
}
.agent-flows-action-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
}
.run-chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.run-chat-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 4px 12px;
}
.run-chat-msg {
  display: flex;
  margin-bottom: 12px;
}
.run-chat-msg.msg-user {
  justify-content: flex-end;
}
.run-chat-msg.msg-bot {
  justify-content: flex-start;
}
.run-chat-msg .msg-bubble {
  max-width: 88%;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.55;
  word-break: break-word;
}
.run-chat-msg .msg-bubble.user {
  background: var(--primary, #2d8cf0);
  color: #fff;
}
.run-chat-msg .msg-bubble.bot {
  background: rgba(0, 0, 0, 0.04);
  color: var(--text, #333);
}
.run-chat-msg .msg-bubble.typing {
  color: var(--subtext2);
}
.run-chat-input-wrap {
  flex-shrink: 0;
  border-top: 1px solid var(--border);
  padding-top: 12px;
}
.run-chat-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
.cursor-blink {
  animation: blink 1s step-end infinite;
}
@keyframes blink {
  50% { opacity: 0; }
}
</style>

<style>
.agent-flow-run-drawer .ivu-drawer-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 16px 16px;
  overflow: hidden;
}
</style>
